<script setup>
import { computed, onMounted, ref } from 'vue'
import { useQueueStore } from '@state/stores/queue'
import { usePlaybackStore } from '@state/stores/playback'
import DataLayer from '@services/data-layer/index'
import modalService from '@/services/modal-service'
import { useAppearanceStore } from '@state/stores/appearance'
import { useAuthStore } from '@state/stores/auth'
import { useQueueService } from '@composables/use-queue-service'

const PLAYLIST_PAGE_SIZE = 20

const props = defineProps({
    action: {
        type: Object,
        default: () => {},
    },
    title: {
        type: String,
        default: 'Learn more...',
    },
})

const appearanceStore = useAppearanceStore()
const authStore = useAuthStore()
const queueStore = useQueueStore()
const playbackStore = usePlaybackStore()
const queueService = useQueueService()

const playlist = ref({})
const playlistMixes = ref([])
const totalMixes = ref(null)

const loggedIn = computed(() => authStore.loggedIn)

const isActionType = type => !!props.action && props.action.type === type

const isPlayingNow = computed(() => {
    const { status } = playbackStore
    return status === 'playing' || status === 'loading'
})

const isChannelPlaying = computed(() => {
    if (!isActionType('playChannel')) return false
    const { resource } = playbackStore
    return isPlayingNow.value && resource && resource.type === 'channel' && resource.id === props.action.value
})

const isMixPlaying = computed(() => {
    if (!isActionType('playMix')) return false
    const { resource } = playbackStore
    return isPlayingNow.value && resource && resource.type === 'mix' && resource.id === props.action.value
})

const isMixLoaded = computed(() => {
    if (!isActionType('playMix')) return false
    const { resource } = playbackStore
    return resource && props.action.value === resource.id
})

const isCurrentPlaylistPlaying = computed(
    () => isActionType('playPlaylist') && Number(props.action.value) === queueStore.playlistId
)

function warn(error) {
    appearanceStore.handleNotification({ message: error.message, type: 'warning' })
}

function handleChannelClick() {
    if (isChannelPlaying.value) {
        queueService.pause()
        return
    }
    if (!loggedIn.value) {
        modalService.show('AUTH_REQUIRED_FOR_PLAYBACK')
        return
    }
    queueService.playRadioChannel(props.action.value).catch(warn)
}

function handleMixClick() {
    if (isMixLoaded.value || isMixPlaying.value) {
        queueService.pause()
        return
    }
    if (!loggedIn.value) {
        modalService.show('AUTH_REQUIRED_FOR_PLAYBACK')
        return
    }
    DataLayer.mixes.getById(props.action.value).then(({ body: mix }) => {
        queueService.playMix(mix).catch(warn)
    })
}

function playPlaylist() {
    if (isCurrentPlaylistPlaying.value) {
        queueService.pause()
        return
    }
    queueService.playMixMultiple({
        mixes: [...playlistMixes.value],
        id: playlist.value.id,
        position: 0,
    })
}

function getPlaylistMixesAndPlay({ playlistId = props.action.value, limit = PLAYLIST_PAGE_SIZE, offset = 0 }) {
    if (totalMixes.value === playlistMixes.value.length) {
        playPlaylist()
        return
    }
    DataLayer.playlists.getRevisionsMixes(playlistId, limit, offset).then(mixes => {
        totalMixes.value = Number(mixes.headers['x-items-total'])
        playlistMixes.value = mixes.body.map(revisionsMixes => revisionsMixes.mixes)
        getPlaylistMixesAndPlay({ limit: totalMixes.value })
    })
}

onMounted(() => {
    if (isActionType('playPlaylist')) {
        DataLayer.playlists.getById(props.action.value).then(response => {
            playlist.value = response.body
        })
    }
})
</script>

<template>
    <div v-if="action">
        <BaseButton
            v-if="action.type === 'playChannel'"
            variant="inverse"
            size="s"
            :title="isChannelPlaying ? 'Pause' : 'Listen Now'"
            :aria-label="isChannelPlaying ? 'Pause' : 'Listen Now'"
            @click="handleChannelClick"
        >{{ isChannelPlaying ? 'Pause' : 'Listen Now' }}</BaseButton>
        <BaseButton
            v-if="action.type === 'playMix'"
            variant="inverse"
            size="s"
            :title="isMixPlaying ? 'Pause' : 'Listen Now'"
            :aria-label="isMixPlaying ? 'Pause' : 'Listen Now'"
            @click="handleMixClick"
        >{{ isMixPlaying ? 'Pause' : 'Listen Now' }}</BaseButton>
        <BaseButton
            v-if="action.type === 'playPlaylist'"
            variant="inverse"
            size="s"
            :title="isCurrentPlaylistPlaying ? 'Pause' : 'Listen Now'"
            :aria-label="isCurrentPlaylistPlaying ? 'Pause' : 'Listen Now'"
            @click="getPlaylistMixesAndPlay({})"
        >{{ isCurrentPlaylistPlaying ? 'Pause' : 'Listen Now' }}</BaseButton>

        <!-- `learn-more-link` makes the link take the click and the hover for the unhoverable
             pill inside it — see the style block. -->
        <BaseLink
            v-if="action.type === 'navigateInternal'"
            class="learn-more-link"
            :title="title"
            :aria-label="title"
            :to="{ path: action.value }"
        >
            <BaseButton
                variant="inverse"
                size="s"
                :title="title"
                :aria-label="title"
            >Learn More</BaseButton>
        </BaseLink>
        <BaseLink
            v-if="action.type === 'navigateExternal'"
            class="learn-more-link"
            :href="action.value"
        >
            <BaseButton
                variant="inverse"
                size="s"
                :title="title"
                :aria-label="title"
            >Learn More</BaseButton>
        </BaseLink>
    </div>
</template>

<style lang="scss" scoped>
@use '@design' as *;

// The link, not the label pill inside it, takes the click: the pill is `cannot-be-hovered`
// (the sanctioned <BaseLink><BaseButton> pattern). An inline link is one text line tall, so
// it is sized to the pill — otherwise the pill's top and bottom bands miss it — and it
// supplies the hover the pill can't have, mirroring BaseButton's inverse hover
// (state/hover-on-light over surface/inverse).
.learn-more-link {
    display: inline-flex;
    border-radius: $radius-full;

    &:hover .base-button-inverse {
        background: linear-gradient($c-state-hover-on-light, $c-state-hover-on-light) $c-surface-inverse;
    }

    // Press mirrors BaseButton's too: pressed overlay + the 0.97 scale, no scale under
    // reduced motion.
    &:active .base-button-inverse {
        background: linear-gradient($c-state-pressed-on-light, $c-state-pressed-on-light) $c-surface-inverse;
        transform: scale(0.97);

        @media (prefers-reduced-motion: reduce) {
            transform: none;
        }
    }
}
</style>
